import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import { useAutoAnimate } from "@formkit/auto-animate/react";

import { cn } from "@/lib/utils";
import {
  customElementAttributeMappers,
  setCustomElementMetadata,
} from "@/lib/customElements";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 focus:bg-primary/70",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:bg-destructive/70",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        "outline-primary":
          "border border-primary bg-background text-primary hover:bg-accent",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:bg-secondary/70",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-7 rounded-md px-3 text-xs",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-6 w-6 rounded-full [&>*]:!h-3 [&>*]:!w-3",
        "icon-sm": "h-4 w-4 rounded-full [&>*]:!h-2 [&>*]:!w-2",
        "icon-lg": "h-8 w-8 rounded-full [&>*]:!h-4 [&>*]:!w-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const [parent] = useAutoAnimate({ duration: 100 });

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={props.disabled || loading}
      >
        {size !== "icon" && size !== "icon-sm" && size !== "icon-lg" ? (
          <span
            ref={parent}
            className="inline-flex items-center justify-between w-full gap-2"
          >
            {children}
            {loading && <LoaderCircle className="animate-spin" />}
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

setCustomElementMetadata({
  component: Button,
  tagName: "raes-button",
  observedAttributes: {
    type: customElementAttributeMappers.string,
    variant: customElementAttributeMappers.string,
    size: customElementAttributeMappers.string,
    disabled: customElementAttributeMappers.boolean,
    loading: customElementAttributeMappers.boolean,
  },
});

export { Button, buttonVariants };
